<template>
    <div class="view-container">
        <modal
            :showDialog="modal == 'DISMISS'"
            @close="close"
            :title="$t('DISMISS_ALARMS')"
        >
            <dismiss-alarm-modal
                :system-id="systemId"
                :is-group="isSelectedGroup"
                :to-dismiss="selectedObject"
                @dismissed="closeAndReload"
            />
        </modal>
        <modal
            :showDialog="modal == 'DISMISSMANY'"
            @close="close"
            :title="$t('DISMISS_ALARMSMANY')"
        >
            <dismiss-alarm-modal
                :system-id="systemId"
                :is-group="isSelectedGroup"
                :is-many="true"
                :to-dismiss="selectedObject"
                @dismissed="closeAndReload"
            />
        </modal>
        <modal
            :showDialog="modal == 'PROBLEM'"
            @close="close"
            :title="$t('PROBLEM_ALARMS')"
        >
            <task-problem-modal
                :system-id="systemId"
                :task="selectedObject"
                :system-name="systemName"
                @saved="saveAndReload"
            />
        </modal>
        <div class="max-grid">
            <ejs-grid
                ref="grid"
                height="100%"
                width="100%"
                :toolbar="toolbar"
                :toolbarClick="toolbarClick"
                :selectionSettings="selectionOptions"
                :allowTextWrap="false"
                :dataBound="dataBound"
                :dataSource="data"
                :allowPaging="true"
                :pageSettings="pageSettings"
                :allowFiltering="true"
                :filterSettings="filterOptions"
                :allowSorting="true"
                :allowMultiSorting="true"
                :allowPdfExport="true"
                :allowCsvExport="true"
                :allowExcelExport="true"
                :showBusyIndicator="true"
                :childGrid="childGrid"
                :rowDataBound="rowDataBound"
                :detailDataBound="onDetailDataBound"
            >
                <e-columns>
                    <e-column
                        type="checkbox"
                        width="50"
                        :headerText="$t('Discontinued')"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                        :canExport="false"
                    ></e-column>
                    <e-column
                        field="createDate"
                        :headerText="$t('CreateDate')"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                        type="dateTime"
                        :format="dateTimeFormatOptions"
                        :filter="dateFilterOptions"
                    ></e-column>
                    <e-column
                        field="priority"
                        width="150"
                        :headerText="$t('Priority')"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                    ></e-column>
                    <e-column
                        field="alarmName"
                        :headerText="$t('AlarmName')"
                        :template="TextTemplate"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                    ></e-column>
                    <e-column
                        field="unitNumber"
                        width="150"
                        :headerText="$t('UnitNumber')"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                    ></e-column>
                    <e-column
                        field="description"
                        :headerText="$t('Description')"
                        :template="TextTemplate"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                    ></e-column>
                    <e-column
                        field="endDateString"
                        :headerText="$t('EndDate')"
                        textAlign="Left"
                        :customAttributes="customAttributes"
                    ></e-column>
                    <e-column
                        v-if="!$store.getters.isUser"
                        :headerText="$t('Options')"
                        width="200px"
                        :template="optionsButtons"
                        :customAttributes="customAttributes"
                    ></e-column>
                </e-columns>
            </ejs-grid>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import {
    CommandColumn,
    CsvExport,
    ExcelExport,
    Filter,
    Page,
    PdfExport,
    Search,
    Sort,
    Toolbar,
    DetailRow,
} from "@syncfusion/ej2-vue-grids";
import { getSystemTasks, dismissAlarms } from "../../api/api";

import DismissAlarmModal from "../../components/Systems/DismissAlarmModal.vue";
import ProbablyOverflowingTextCell from "../../components/Systems/ProbablyOverflowingTextCell.vue";
import Modal from "../../components/Modal.vue";
import TaskProblemModal from "../../components/Settings/Tasks/TaskProblemModal.vue";

let context = null;

export default {
    props: {
        systemName: {
            required: false,
            type: String,
        },
        systemId: {
            required: false,
            type: String,
        },
        priority: {
            required: false,
            type: String,
        },
    },
    provide: {
        grid: [
            Sort,
            Page,
            Filter,
            Toolbar,
            Search,
            CommandColumn,
            PdfExport,
            CsvExport,
            ExcelExport,
            DetailRow,
        ],
    },
    components: {
        DismissAlarmModal,
        ProbablyOverflowingTextCell,
        Modal,
        TaskProblemModal,
    },
    computed: {
        toolbar() {
            if (this.$store.getters.isUser) {
                return [
                    {
                        text: this.$t("ClearFilter"),
                        id: "clear-filters",
                        prefixIcon: "e-cancel",
                        align: "Right",
                    },
                    {
                        text: this.showResolved
                            ? this.$t("ShowUnresolvedTasks")
                            : this.$t("ShowResolvedTasks"),
                        id: "toggle-resolved-tasks",
                        prefixIcon: "e-check",
                        align: "Right",
                    }, // Updated button
                    "Search",
                ];
            }

            return [
                ...(!this.showResolved
                    ? [
                          {
                              text: this.$t("DismissMany"),
                              id: "dismiss-many",
                              prefixIcon: "e-cancel",
                              align: "Left",
                          },
                      ]
                    : []),
                {
                    text: this.$t("ClearFilter"),
                    id: "clear-filters",
                    prefixIcon: "e-cancel",
                    align: "Right",
                },
                {
                    text: this.showResolved
                        ? this.$t("ShowUnresolvedTasks")
                        : this.$t("ShowResolvedTasks"),
                    id: "toggle-resolved-tasks",
                    prefixIcon: "e-check",
                    align: "Right",
                }, // Updated button
                "Search",
            ];
        },
        notUser() {
            return this.$store.getters.isAdmin || this.$store.getters.isService;
        },
    },
    data() {
        return {
            data: [],
            childData: [],
            pageSettings: {
                pageSize: 100,
                pageSizes: [10, 50, 100, 500, 1000],
            },
            showResolved: false,
            selectionOptions: { type: "Multiple" },
            filterOptions: {
                type: "CheckBox",
                columns:
                    this.priority != null
                        ? [
                              {
                                  field: "priority",
                                  matchCase: false,
                                  operator: "equal",
                                  value: this.priority,
                              },
                          ]
                        : [],
            },
            // Data wymaga filtru Menu - CheckBox listuje kazdy timestamp z dokladnoscia
            // do sekundy, wiec nie da sie nim filtrowac zakresem.
            dateFilterOptions: { type: "Menu" },
            customAttributes: { class: "customcss" },
            dateTimeFormatOptions: {
                format: "yyyy-MM-dd HH:mm:ss",
                type: "dateTime",
            },
            selectedObject: null,
            isSelectedGroup: false,
            modal: "NONE",
            childGrid: {
                queryString: "table_id",
                columns: [
                    {
                        field: "name",
                        headerText: this.$i18n.t("Name"),
                        textAlign: "Left",
                    },
                    {
                        field: "description",
                        headerText: this.$i18n.t("Description"),
                        textAlign: "Left",
                        template: function () {
                            return {
                                template: {
                                    extends: ProbablyOverflowingTextCell,
                                },
                            };
                        },
                    },
                    {
                        field: "createDate",
                        headerText: this.$i18n.t("CreateDate"),
                        textAlign: "Left",
                        type: "dateTime",
                        format: "yyyy-MM-dd HH:mm:ss",
                    },
                    {
                        field: "endDate",
                        headerText: this.$i18n.t("EndDate"),
                        textAlign: "Left",
                        type: "dateTime",
                        format: "yyyy-MM-dd HH:mm:ss",
                    },
                    {
                        field: "dismissDate",
                        visible:
                            this.$store.getters.isAdmin ||
                            this.$store.getters.isService,
                        headerText: this.$i18n.t("Options"),
                        textAlign: "Left",
                        template: function () {
                            return {
                                template: Vue.component("optionsButtons", {
                                    template: `
                                        <button class='show-password-button' style="align-self: center;" v-on:click="onClick('dismiss')">${context.$t(
                                            "Dismiss"
                                        )}</button>
                                    `,
                                    data: function () {
                                        return {
                                            data: {},
                                        };
                                    },
                                    methods: {
                                        onClick(mode) {
                                            switch (mode) {
                                                case "dismiss":
                                                    context.isSelectedGroup = true;
                                                    context.selectedObject =
                                                        this.data;
                                                    context.modal = "DISMISS";
                                                    break;
                                            }
                                        },
                                    },
                                }),
                            };
                        },
                    },
                ],
            },
            optionsButtons: function () {
                return {
                    template: Vue.component("optionsButtons", {
                        template: `
                        <div class="horizontal">
                            <button v-if="!showResolved" class='show-password-button' style="align-self: center;" v-on:click="onClick('dismiss')">{{ $t('Dismiss') }}</button>
                            <button v-if="!showResolved" class='show-password-button' style="align-self: center; margin-left: 5px;" v-on:click="onClick('problem')">{{ $t('Problem') }}</button>
                        </div>
                    `,
                        data: function () {
                            return {
                                data: {},
                            };
                        },
                        methods: {
                            onClick(mode) {
                                switch (mode) {
                                    case "dismiss":
                                        context.modal = "DISMISS";
                                        context.selectedObject = this.data;
                                        break;
                                    case "problem":
                                        context.modal = "PROBLEM";
                                        context.selectedObject = this.data;
                                }
                            },
                        },
                        computed: {
                            showResolved: function () {
                                return context.showResolved;
                            },
                        },
                    }),
                };
            },
            TextTemplate: function () {
                return {
                    template: {
                        extends: ProbablyOverflowingTextCell,
                    },
                };
            },
        };
    },
    methods: {
        toDateOrNull(value) {
            if (value == null) {
                return null;
            }

            let parsed = value instanceof Date ? value : new Date(value);

            if (isNaN(parsed.getTime()) || parsed < new Date("2001-01-01")) {
                return null;
            }

            return parsed;
        },
        async getData() {
            this.$store.dispatch("set_loading", true);
            this.childData = [];

            getSystemTasks(this.systemId, this.showResolved).then(
                (response) => {
                    this.data = response.data;
                    this.data.forEach((el) => {
                        var minDate = new Date("2001-01-01");
                        if (new Date(el.endDate) < minDate) {
                            el.endDateString = "-";
                        } else {
                            el.endDateString = el.endDate.toDateTime();
                        }
                        if (el.unitNumber == null) {
                            el.unitNumber = "-";
                        }
                        el.priority = [
                            this.$t("High"),
                            this.$t("Mid"),
                            this.$t("Low"),
                        ][el.priority - 1];
                        el.table_id = `${el.alarmName}_${el.unitNumber}_${el.createDate}`;
                        // API zwraca daty jako stringi ISO, a kolumny typu dateTime
                        // filtruja i sortuja tylko obiekty Date.
                        el.createDate = this.toDateOrNull(el.createDate);
                        if (
                            el.nestedAlarms != undefined &&
                            el.nestedAlarms != null
                        ) {
                            el.nestedAlarms.forEach((ne) => {
                                ne.table_id = el.table_id;
                                ne.alarmTypeId = el.alarmTypeId;
                                ne.createDate = this.toDateOrNull(ne.createDate);
                                ne.endDate = this.toDateOrNull(ne.endDate);
                            });
                            this.childData.push(...el.nestedAlarms);
                        }

                        el.alarmName = this.$t(el.alarmName);
                    });

                    // this.data.sort((b, a) => new Date(a.createDate) - new Date(b.createDate))
                    this.data.sort((b, a) =>
                        b.alarmName.localeCompare(a.alarmName)
                    );

                    this.$store.dispatch("set_loading", false);
                }
            );
        },
        dataBound: function () {
            this.$refs.grid.autoFitColumns();
        },
        toolbarClick: function (args) {
            switch (args.item.id) {
                case "clear-filters":
                    this.$refs.grid.clearFiltering();
                    break;
                case "dismiss-many":
                    this.dismissManySetup();
                    break;
                case "toggle-resolved-tasks":
                    this.showResolved = !this.showResolved;
                    args.item.text = this.showResolved
                        ? this.$t("ShowUnresolvedTasks")
                        : this.$t("ShowResolvedTasks");
                    this.getData();
                    break;
            }
        },
        // Zadanie bez zagnieżdżonych alarmów nie ma czego rozwijać. Komórka
        // rozwijania zostaje z klasą paczki, więc ma tę samą szerokość, tło,
        // obramowanie i hover co pozostałe wiersze (w obu motywach) — dopisujemy
        // tylko task-no-details, która chowa strzałkę i blokuje klik (style niżej).
        rowDataBound(args) {
            const hasNestedAlarms =
                args.data.nestedAlarms != null &&
                args.data.nestedAlarms.length > 0;
            if (hasNestedAlarms) return;

            const expandCell = args.row.querySelector(".e-detailrowcollapse");
            if (expandCell) expandCell.classList.add("task-no-details");
        },
        dismissManySetup() {
            this.modal = "DISMISSMANY";
            this.selectedObject = this.$refs.grid.getSelectedRecords();
        },
        onDetailDataBound(args) {
            args.detailElement.querySelector(
                ".e-grid"
            ).ej2_instances[0].dataSource = this.childData.filter(
                (el) => el.table_id == args.data.table_id
            );
        },
        close() {
            this.modal = "NONE";
            this.selectedObject = null;
            this.isSelectedGroup = false;
        },
        closeAndReload() {
            this.modal = "NONE";

            this.selectedObject = null;
            this.isSelectedGroup = false;

            this.getData();
        },
        saveAndReload() {
            let objToSend = {
                Annotation: "Autodismissed by creating problem.",
                AlarmsId: [],
                AlarmTypeId: -1,
            };

            if (this.selectedObject.nestedAlarms != null) {
                objToSend.AlarmsId.push(
                    ...this.selectedObject.nestedAlarms.map((m) => m.id)
                );
                objToSend.AlarmTypeId = this.selectedObject.alarmTypeId;
            } else {
                objToSend.AlarmsId.push(this.selectedObject.id);
                objToSend.AlarmTypeId = this.selectedObject.alarmTypeId;
            }

            dismissAlarms(this.systemId, objToSend)
                .then(() => {
                    this.$emit("dismissed");
                })
                .catch((err) => {
                    this.error = err.response.data.error;
                });

            this.closeAndReload();
        },
    },
    mounted: function () {
        context = this;
        this.getData();
    },
};
</script>

<style scoped>
.e-grid .e-headercell.customcss {
    padding: 0 5px 0 5px;
}
</style>

<style>
.tippy-tooltip.customTheme-theme {
    background-color: #346f8b;
}

.tippy-popper[x-placement^="left"]
    .tippy-tooltip.customTheme-theme
    .tippy-arrow {
    border-left: 8px solid #346f8b;
}

/*
 * Pusta komórka rozwijania (rowDataBound). Klik przechodzi przez komórkę na
 * wiersz, a paczka rozwija tylko po kliknięciu w td (closest(target, 'td')),
 * więc pusta tabela zagnieżdżona się nie otworzy.
 */
.e-grid td.e-detailrowcollapse.task-no-details {
    pointer-events: none;
    cursor: default;
}

.e-grid td.e-detailrowcollapse.task-no-details > * {
    visibility: hidden;
}

.show-password-button {
    border: 1px #ddd solid;
    padding: 5px 10px;
    background: none;
}

.show-password-button:hover {
    background: #bbbbbb;
}
</style>



// WEBPACK FOOTER //
// src/views/System/SystemTasks.vue